import { createRoot } from 'react-dom/client'
import * as Sentry from "@sentry/react";
import App from './App.tsx'
import './index.css'
import './calendar-fix.css'
import 'react-toastify/dist/ReactToastify.css'

// Global error handlers
const setupGlobalErrorHandlers = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Handle token refresh errors specifically
    if (event.reason && typeof event.reason === 'string' && 
        event.reason.includes('Failed to refresh token')) {
      console.log('🔐 Handling unhandled token refresh error...');
      event.preventDefault(); // Prevent the default error handling
      
      // Don't send token refresh errors to Sentry - they're not real app errors
      // Just log and redirect to login
      console.log('🔐 Token refresh failed, redirecting to login');
      
      // Redirect to login page
      window.location.href = '/';
      return;
    }
    
    // Handle authentication errors
    if (event.reason && typeof event.reason === 'string' && 
        event.reason.includes('Authentication failed')) {
      console.log('🔐 Handling unhandled authentication error...');
      event.preventDefault(); // Prevent the default error handling
      
      // Redirect to login page
      window.location.href = '/';
      return;
    }
    
    // Handle Mapbox dynamic import errors
    if (event.reason && typeof event.reason === 'string' && 
        (event.reason.includes('Failed to fetch dynamically imported module') ||
         event.reason.includes('mapbox'))) {
      console.log('🗺️ Handling Mapbox dynamic import error...');
      event.preventDefault();
      
      // Don't send Mapbox loading errors to Sentry - they're network/CDN issues
      console.log('🗺️ Mapbox module failed to load, this is likely a network/CDN issue');
      return;
    }
    
    // Handle Keycloak initialization errors
    if (event.reason && typeof event.reason === 'string' && 
        event.reason.includes('A \'Keycloak\' instance can only be initialized once')) {
      console.log('🔐 Handling Keycloak initialization error...');
      event.preventDefault();
      
      // Don't send Keycloak initialization errors to Sentry - they're expected
      console.log('🔐 Keycloak already initialized, this is expected behavior');
      return;
    }
    
    // For other errors, let Sentry handle them
    Sentry.captureException(event.reason);
  });

  // Handle general errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    
    // Handle Mapbox-specific errors
    if (event.error && event.error.message && 
        (event.error.message.includes('Cannot read properties of undefined') ||
         event.error.message.includes('mapbox') ||
         event.error.message.includes('Failed to fetch dynamically imported module'))) {
      console.warn('🗺️ Mapbox error detected, preventing crash:', event.error.message);
      event.preventDefault();
      return;
    }
    
    // Handle Keycloak-specific errors
    if (event.error && event.error.message && 
        event.error.message.includes('A \'Keycloak\' instance can only be initialized once')) {
      console.warn('🔐 Keycloak initialization error detected, preventing crash:', event.error.message);
      event.preventDefault();
      return;
    }
    
    Sentry.captureException(event.error);
  });
};

// Initialize Sentry
Sentry.init({
  dsn: "https://1ddc2d09ad2ed7e238ab93b52a761f69@o4509719395368960.ingest.us.sentry.io/4509719397072896",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Enable performance monitoring
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0,
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Show user feedback dialog on errors
  beforeSend(event) {
    // Show feedback dialog for errors
    Sentry.showReportDialog({
      eventId: event.event_id,
      title: "Something went wrong",
      subtitle: "Help us fix this issue",
      subtitle2: "Describe what you were doing when the error occurred",
      labelName: "Name",
      labelEmail: "Email",
      labelComments: "What happened?",
      labelClose: "Close",
      labelSubmit: "Send report",
      errorGeneric: "An error occurred while sending the report. Please try again.",
      errorFormEntry: "Some fields are filled incorrectly. Fix the errors and try again.",
      successMessage: "Thank you! Your report has been sent."
    });
    return event;
  }
});

// Setup global error handlers
setupGlobalErrorHandlers();

createRoot(document.getElementById('root')!).render(
  <>
    <App />
  </>,
)
